import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
const TOKEN_KEY = "mm_auth_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // refresh user on mount
  useEffect(() => {
    // Skip /me check if we're in the OAuth callback flow – AuthCallback will set the user.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!token) { setLoading(false); return; }
    api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const persist = useCallback((t, u) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
    }
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    persist(r.data.token, r.data.user);
    return r.data.user;
  }, [persist]);

  const register = useCallback(async (data) => {
    const r = await api.post("/auth/register", data);
    persist(r.data.token, r.data.user);
    return r.data.user;
  }, [persist]);

  const googleSession = useCallback(async (session_id) => {
    const r = await api.post("/auth/google/session", { session_id });
    persist(r.data.token, r.data.user);
    return r.data.user;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const r = await api.patch("/auth/me", patch);
    setUser(r.data);
    return r.data;
  }, []);

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, register, googleSession, logout, updateProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside AuthProvider");
  return v;
}
